import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const formatDateInput = (date) => {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDeliveryDate = () => {
  const today = new Date();
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + 28);
  return formatDateInput(futureDate);
};

const getMinimumDeliveryDate = () => {
  const today = new Date();
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + 15);
  return formatDateInput(futureDate);
};

const getDeliveryDiscount = (subtotal) => {
  if (subtotal >= 15000) return 750;
  if (subtotal >= 11500) return 500;
  if (subtotal >= 7500) return 400;
  return 0;
};

const normalizeImageUrl = (url) => {
  if (!url) return '';
  const value = String(url).trim();
  if (!value) return '';

  const safeValue = value
    .replace(/\.heic(?=[?#]|$)/gi, '.jpg')
    .replace(/\.heif(?=[?#]|$)/gi, '.jpg');

  if (safeValue.includes('cloudinary.com') && !/[?&]fm=jpg/i.test(safeValue)) {
    return `${safeValue}${safeValue.includes('?') ? '&' : '?'}fm=jpg`;
  }

  return safeValue;
};

export default function Cart() {
  const navigate = useNavigate();
  const { cart, updateCartItem, removeCartItem } = useCart();

  const defaultDeliveryDate = useMemo(() => getDefaultDeliveryDate(), []);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(() => {
    const savedDate = localStorage.getItem('starlight-delivery-date');
    return savedDate && savedDate >= getMinimumDeliveryDate() ? savedDate : defaultDeliveryDate;
  });

  const updateQuantity = (item, change) => {
    updateCartItem(item, Math.max(1, Number(item.quantity || 1) + change));
  };

  const removeItem = (item) => {
    removeCartItem(item);
  };

  const subtotal = cart.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0);
  const today = new Date();
  const selectedDate = selectedDeliveryDate ? new Date(`${selectedDeliveryDate}T00:00:00`) : new Date();
  const daysAhead = Math.max(0, Math.ceil((selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  const deliveryDiscount = daysAhead >= 30 ? getDeliveryDiscount(subtotal) : 0;
  const finalSubtotal = Math.max(0, subtotal - deliveryDiscount);
  const shippingCost = cart.reduce((sum, item) => sum + Number(item.shippingCharge ?? 400), 0);

  const handleDeliveryDateChange = (event) => {
    const nextDate = event.target.value;
    setSelectedDeliveryDate(nextDate);
    if (nextDate) {
      localStorage.setItem('starlight-delivery-date', nextDate);
    }
  };

  const askOnWhatsApp = () => {
    const productLines = cart.map((item) => (
      `${item.name} x${Number(item.quantity || 1)}\n${window.location.origin}/product/${item._id}`
    )).join('\n\n');
    const message = productLines
      ? `Hi, I would like to ask about these products:\n\n${productLines}`
      : `Hi, I would like to know more about Starlight Threading outfits.\n${window.location.href}`;
    window.open(`https://wa.me/918309158686?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="cart-page">
      <div className="cart-header-block">
        <h1>Shopping Cart</h1>
        <p>{cart.length} item in your cart</p>
      </div>

      <div className="cart-layout">
        <div className="cart-items-panel">
          {cart.length === 0 ? (
            <div className="empty-cart-state">
              <p>Your cart is empty.</p>
              <button type="button" className="primary-btn" onClick={() => navigate('/')}>
                Continue Shopping
              </button>
            </div>
          ) : (
            cart.map((item) => (
              <div className="cart-item-card" key={item.cartItemId || `${item._id}-${item.size || 'default'}-${item.color || 'default'}-${item.ageGroup || 'default'}`}>
                <img src={normalizeImageUrl(item.cartImage || item.image || item.images?.[0]?.url || 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=800&q=80')} alt={item.name} />

                <div className="cart-item-info">
                  <div className="cart-item-row">
                    <div>
                      <h3>{item.name}</h3>
                      <p>Size: {item.size || 'Standard'} · Age: {item.ageGroup || '1-3'} </p>
                    </div>
                    <button type="button" className="remove-item" onClick={() => removeItem(item)}>×</button>
                  </div>

                  <div className="cart-item-controls">
                    <div className="quantity-box mini-box">
                      <button type="button" onClick={() => updateQuantity(item, -1)}>-</button>
                      <span>{item.quantity || 1}</span>
                      <button type="button" onClick={() => updateQuantity(item, 1)}>+</button>
                    </div>

                    <div className="cart-item-price">₹{Number(item.price || 0).toLocaleString('en-IN')}</div>
                  </div>
                </div>
              </div>
            ))
          )}

          <div className="date-block">
            <h2>When do you need these?</h2>
            <p>
              Starlight Threading outfits are made to order. Select your preferred dispatch date.
            </p>

            <div className="date-fields">
              <div className="date-field">
                <label>Dispatch Date</label>
                <input type="date" value={selectedDeliveryDate} min={getMinimumDeliveryDate()} onChange={handleDeliveryDateChange} />
              </div>
            </div>
            {deliveryDiscount > 0 && (
              <p className="shipping-note">30-day advance booking discount applied: ₹{deliveryDiscount.toLocaleString('en-IN')} off your cart.</p>
            )}
          </div>
        </div>

        <aside className="cart-summary-panel">
          <h2>Order Summary</h2>

          <div className="summary-row">
            <span>Subtotal</span>
            <strong>₹{subtotal.toLocaleString('en-IN')}</strong>
          </div>

          {deliveryDiscount > 0 && (
            <div className="summary-row">
              <span>Advance booking discount</span>
              <strong>-₹{deliveryDiscount.toLocaleString('en-IN')}</strong>
            </div>
          )}

          <div className="summary-row">
            <span>Shipping</span>
            <strong>₹{shippingCost.toLocaleString('en-IN')}</strong>
          </div>

          <p className="shipping-note">
            Shipping is calculated from each product's catalog charge. Products with no charge ship free.
          </p>

          <div className="summary-total">
            <span>Subtotal</span>
            <strong>₹{finalSubtotal.toLocaleString('en-IN')}</strong>
          </div>

          <div className="summary-actions">
            <button type="button" className="primary-btn checkout-button" onClick={() => {
              const confirmed = window.confirm(`Please confirm your dispatch date: ${selectedDeliveryDate}. Continue to checkout?`);
              if (confirmed) navigate('/checkout');
            }}>
              Proceed to Checkout
            </button>
            <button type="button" className="whatsapp-btn summary-whatsapp" onClick={askOnWhatsApp}>
              Ask on WhatsApp
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
